import type {
  ConversationDetail,
  LaunchContext,
  PreparedConversation,
  StartedConversation,
} from '../types'

const API_PREFIX = '/api/v1/ai-conversation'

export class CallApiError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly status: number,
  ) {
    super(message)
    this.name = 'CallApiError'
  }
}

async function request<T>(
  path: string,
  options: { method?: 'GET' | 'POST'; body?: unknown; keepalive?: boolean } = {},
): Promise<T> {
  const response = await fetch(`${API_PREFIX}${path}`, {
    method: options.method ?? 'GET',
    credentials: 'include',
    headers: options.body ? { 'content-type': 'application/json' } : undefined,
    body: options.body ? JSON.stringify(options.body) : undefined,
    keepalive: options.keepalive,
    cache: 'no-store',
  })
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as
      | { code?: string; message?: string }
      | null
    throw new CallApiError(
      payload?.message ?? '通话服务暂时不可用',
      payload?.code ?? 'VOICE_CALL_REQUEST_FAILED',
      response.status,
    )
  }
  return response.json() as Promise<T>
}

export const callApi = {
  exchange(ticket: string) {
    return request<LaunchContext>('/launch/exchange', {
      method: 'POST',
      body: { ticket },
    })
  },
  prepare(id: string) {
    return request<PreparedConversation>('/prepare', { method: 'POST', body: { id } })
  },
  start(id: string) {
    return request<StartedConversation>('/start', { method: 'POST', body: { id } })
  },
  heartbeat(id: string, clientState: string) {
    return request<ConversationDetail>('/heartbeat', {
      method: 'POST',
      body: { id, clientState },
    })
  },
  stop(id: string, reason: string, keepalive = false) {
    return request<{ success: boolean; conversation: ConversationDetail }>('/stop', {
      method: 'POST',
      body: { id, reason },
      keepalive,
    })
  },
  detail(id: string) {
    return request<ConversationDetail>(`/detail?id=${encodeURIComponent(id)}`)
  },
}
